import React, { useState, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Badge,
  Table,
  Form,
  InputGroup,
  Modal,
  Toast,
  ToastContainer,
  Spinner,
  Pagination,
  Alert,
} from 'react-bootstrap';
import {
  FileText,
  CheckCircle,
  Search,
  Clock,
  Cash,
  People,
  InfoCircle,
  Check,
  XCircle,
  ChevronDown,
  ChevronUp,
} from 'react-bootstrap-icons';
import { useAuth } from '../../contexts/AuthContext';

// Données fictives des demandes de remboursement
const mockRequests = [
  { id: 'R001', date: '2025-03-12', patientName: 'Jean Dupont', patientId: 'CNAMGS-123456', amount: 15000, status: 'pending', prescriptionId: 'P001' },
  { id: 'R002', date: '2025-03-11', patientName: 'Marie Nzogho', patientId: 'CNAMGS-789012', amount: 8000, status: 'pending', prescriptionId: 'P002' },
  { id: 'R003', date: '2025-03-10', patientName: 'Pierre Mba', patientId: 'CNAMGS-345678', amount: 12000, status: 'pending', prescriptionId: 'P003' },
  { id: 'R004', date: '2025-03-09', patientName: 'Sophie Nguema', patientId: 'CNAMGS-111222', amount: 5000, status: 'submitted', prescriptionId: 'P004' },
  { id: 'R005', date: '2025-03-08', patientName: 'Paul Obiang', patientId: 'CNAMGS-333444', amount: 9500, status: 'pending', prescriptionId: 'P005' },
  { id: 'R006', date: '2025-03-07', patientName: 'Claire Moussavou', patientId: 'CNAMGS-555666', amount: 22000, status: 'submitted', prescriptionId: 'P006' },
];

const ReimbursementRequests = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState(mockRequests);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (id) => {
    const newSet = new Set(expandedRows);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setExpandedRows(newSet);
  };

  const showToast = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 4000);
  };

  // Filtrer les demandes en attente
  const filteredRequests = useMemo(() => {
    let filtered = requests.filter(r => r.status === 'pending');
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(r =>
        r.id.toLowerCase().includes(term) ||
        r.patientName.toLowerCase().includes(term) ||
        r.patientId.toLowerCase().includes(term)
      );
    }
    return filtered;
  }, [requests, searchTerm]);

  // Pagination
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const paginatedRequests = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredRequests.slice(start, start + itemsPerPage);
  }, [filteredRequests, currentPage, itemsPerPage]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const getStatusBadge = (status) => {
    if (status === 'pending') return <Badge bg="warning" className="rounded-pill px-3 py-2"><Clock size={12} className="me-1" /> En attente</Badge>;
    if (status === 'submitted') return <Badge bg="success" className="rounded-pill px-3 py-2"><CheckCircle size={12} className="me-1" /> Soumise</Badge>;
    return <Badge bg="secondary">{status}</Badge>;
  };

  const handleSubmit = (request) => {
    setSelectedRequest(request);
    setShowConfirmModal(true);
  };

  const confirmSubmit = () => {
    setLoading(true);
    setTimeout(() => {
      setRequests(prev =>
        prev.map(r =>
          r.id === selectedRequest.id ? { ...r, status: 'submitted' } : r
        )
      );
      showToast(`Demande ${selectedRequest.id} soumise avec succès`, 'success');
      setShowConfirmModal(false);
      setSelectedRequest(null);
      setLoading(false);
    }, 800);
  };

  // Statistiques
  const pendingCount = filteredRequests.length;
  const totalPendingAmount = filteredRequests.reduce((sum, r) => sum + r.amount, 0);

  const statsCards = [
    {
      title: 'Demandes en attente',
      value: pendingCount,
      icon: FileText,
      color: '#f59e0b',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      bg: 'white',
    },
    {
      title: 'Montant total en attente',
      value: formatCurrency(totalPendingAmount),
      icon: Cash,
      color: '#4361ee',
      gradient: 'linear-gradient(135deg, #4361ee 0%, #3b2a9f 100%)',
      bg: 'white',
    },
  ];

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const items = [];
    for (let i = 1; i <= totalPages; i++) {
      items.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>
          {i}
        </Pagination.Item>
      );
    }
    return (
      <div className="d-flex justify-content-end mt-3">
        <Pagination size="sm">
          <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
          {items}
          <Pagination.Next onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
        </Pagination>
      </div>
    );
  };

  return (
    <div className="" style={{ fontFamily: "'Inter', sans-serif" }}>
      <ToastContainer className="p-3" style={{ zIndex: 9999, position: 'fixed', top: '20px', right: '20px' }}>
        <Toast show={toast.show} onClose={() => setToast(prev => ({ ...prev, show: false }))} bg={toast.variant} delay={4000} autohide>
          <Toast.Header closeButton={false}>
            <strong className="me-auto">
              {toast.variant === 'success' ? 'Succès' : toast.variant === 'danger' ? 'Erreur' : 'Information'}
            </strong>
          </Toast.Header>
          <Toast.Body className="text-white">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="container-fluid">
        {/* En-tête */}
        <div className="mb-4">
          <h4 className="main-title mb-0 d-flex align-items-center gap-2">
            <div className="bg-primary bg-opacity-10 rounded-circle p-2">
              <FileText size={24} className="text-primary" />
            </div>
            Demandes de remboursement
          </h4>
          <p className="text-muted mb-0 mt-1">
            Consultez et soumettez les demandes de remboursement des patients
          </p>
        </div>

        {/* Cartes de statistiques */}
        <Row className="g-3 mb-4">
          {statsCards.map((card, idx) => (
            <Col md={6} xs={6} key={idx}>
              <Card className="border-0 shadow-sm stats-card h-100" style={{ borderRadius: '16px', background: card.bg }}>
                <div className="card-gradient" style={{ background: card.gradient, height: '4px' }}></div>
                <Card.Body className="p-3 d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h6 className="text-muted mb-1 small fw-semibold text-uppercase" style={{ letterSpacing: '0.5px' }}>
                        {card.title}
                      </h6>
                      <h3 className="mb-0 fw-bold" style={{ fontSize: '1.8rem' }}>
                        {card.value}
                      </h3>
                    </div>
                    <div className="icon-container rounded-3 d-flex align-items-center justify-content-center" style={{ background: `${card.color}20`, width: '48px', height: '48px' }}>
                      <card.icon size={28} style={{ color: card.color }} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Barre de recherche et options */}
        <Card className="border-0 shadow-sm mb-4 bg-white" style={{ borderRadius: '16px' }}>
          <Card.Body className="p-4">
            <Row className="align-items-center g-3">
              <Col md={6}>
                <InputGroup>
                  <InputGroup.Text className="bg-light border-0">
                    <Search size={18} className="text-muted" />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Rechercher par patient, numéro d'ordonnance ou numéro d'assuré..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="border-0 bg-light"
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border-0 bg-light"
                >
                  <option value={5}>5 par page</option>
                  <option value={10}>10 par page</option>
                  <option value={20}>20 par page</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <div className="text-muted small text-md-end">
                  {filteredRequests.length} demande(s) en attente
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Tableau des demandes avec adaptation mobile */}
        <Card className="border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
          <Card.Header className="bg-white border-0 pt-4 pb-4 px-4">
            <h5 className="fw-semibold mb-0">Demandes en attente</h5>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead className="bg-light">
                  <tr>
                    <th>Date</th>
                    <th>Patient</th>
                    <th className="d-none d-sm-table-cell">Montant</th>
                    <th className="d-none d-sm-table-cell">Statut</th>
                    <th className="d-none d-sm-table-cell">Actions</th>
                    <th className="d-sm-none">Détails</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRequests.length > 0 ? (
                    paginatedRequests.map(r => {
                      const isExpanded = expandedRows.has(r.id);
                      return (
                        <React.Fragment key={r.id}>
                          {/* Ligne principale */}
                          <tr className="align-middle">
                            <td data-label="Date" className="small">{formatDate(r.date)}</td>
                            <td data-label="Patient" className="fw-medium">
                              {r.patientName}<br /><small className="text-muted">{r.patientId}</small>
                            </td>
                            <td data-label="Montant" className="fw-bold text-success d-none d-sm-table-cell">{formatCurrency(r.amount)}</td>
                            <td data-label="Statut" className="d-none d-sm-table-cell">{getStatusBadge(r.status)}</td>
                            <td data-label="Actions" className="d-none d-sm-table-cell">
                              <Button
                                size="sm"
                                variant="success"
                                className="rounded-pill px-3"
                                onClick={() => handleSubmit(r)}
                              >
                                <CheckCircle size={14} className="me-1" /> Soumettre
                              </Button>
                            </td>
                            <td className="d-sm-none text-center" style={{ verticalAlign: 'middle' }}>
                              <Button
                                variant="link"
                                onClick={() => toggleRow(r.id)}
                                className="p-0 text-secondary"
                                style={{ textDecoration: 'none' }}
                              >
                                {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                              </Button>
                            </td>
                          </tr>
                          {/* Ligne de détails (visible seulement sur mobile et si dépliée) */}
                          {isExpanded && (
                            <tr className="d-sm-none detail-row">
                              <td colSpan="6" className="p-3 bg-light">
                                <div className="row g-2">
                                  <div className="col-6"><strong>Montant :</strong> {formatCurrency(r.amount)}</div>
                                  <div className="col-6"><strong>Statut :</strong> {getStatusBadge(r.status)}</div>
                                  <div className="col-12 mt-2">
                                    <Button
                                      size="sm"
                                      variant="success"
                                      className="rounded-pill"
                                      onClick={() => handleSubmit(r)}
                                    >
                                      <CheckCircle size={14} className="me-1" /> Soumettre
                                    </Button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center py-5">
                        <FileText size={48} className="text-muted mb-3" />
                        <p className="text-muted">Aucune demande de remboursement en attente</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
            {renderPagination()}
          </Card.Body>
        </Card>

        {/* Information */}
        <Row className="mt-4">
          <Col md={12}>
            <Alert variant="info" className="border-0 rounded-3 shadow-sm" style={{ background: 'linear-gradient(135deg, #e8f4fd 0%, #d9eaff 100%)' }}>
              <div className="d-flex align-items-center gap-3">
                <InfoCircle size={24} className="text-info" />
                <div>
                  <strong className="d-block mb-1">Soumission des demandes</strong>
                  <span className="small">
                    Après délivrance de l'ordonnance, la demande de remboursement apparaît ici. Vous pouvez la soumettre pour traitement par la CNAMGS.
                  </span>
                </div>
              </div>
            </Alert>
          </Col>
        </Row>

        {/* Modal de confirmation (inchangé) */}
        <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
          <Modal.Header closeButton className="bg-primary text-white">
            <Modal.Title className="d-flex align-items-center gap-2">
              <CheckCircle size={20} />
              Confirmer la soumission
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedRequest && (
              <>
                <div className="mb-3 p-3 rounded-3" style={{ background: '#e8f5e9', borderLeft: '4px solid #10b981' }}>
                  <div className="d-flex align-items-center gap-2">
                    <People size={18} className="text-success" />
                    <strong>Patient :</strong> {selectedRequest.patientName}
                    <span className="text-muted">({selectedRequest.patientId})</span>
                  </div>
                </div>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <small className="text-muted d-block">Date</small>
                      <strong>{formatDate(selectedRequest.date)}</strong>
                    </div>
                    <div className="mb-3">
                      <small className="text-muted d-block">Montant</small>
                      <strong className="text-success">{formatCurrency(selectedRequest.amount)}</strong>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <small className="text-muted d-block">Ordonnance associée</small>
                      <strong>{selectedRequest.prescriptionId}</strong>
                    </div>
                  </Col>
                </Row>
                <Alert variant="info" className="mt-2">
                  <InfoCircle size={14} className="me-1" />
                  La demande sera envoyée à la CNAMGS pour traitement.
                </Alert>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowConfirmModal(false)} className="rounded-pill">
              Annuler
            </Button>
            <Button variant="primary" onClick={confirmSubmit} disabled={loading} className="rounded-pill px-4">
              {loading ? <Spinner size="sm" className="me-1" /> : <Check size={16} className="me-1" />}
              {loading ? 'Soumission...' : 'Confirmer'}
            </Button>
          </Modal.Footer>
        </Modal>

        <style>{`
          .stats-card {
            position: relative;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            border-radius: 16px;
            overflow: hidden;
          }
          .stats-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12) !important;
          }
          .card-gradient {
            transition: height 0.2s ease;
          }
          .stats-card:hover .card-gradient {
            height: 6px;
          }
          .icon-container {
            transition: transform 0.2s ease;
          }
          .stats-card:hover .icon-container {
            transform: scale(1.05);
          }
          .table > :not(caption) > * > * {
            padding: 0.75rem 0.75rem;
            vertical-align: middle;
          }
          @media (max-width: 768px) {
            .table td, .table th {
              padding: 0.5rem;
            }
            .btn-sm {
              padding: 0.25rem 0.5rem;
              font-size: 0.75rem;
            }
            .detail-row td {
              padding: 0.75rem;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default ReimbursementRequests;